import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { localization } from '@ocean/configs'
import { AppThunk } from 'store'
import { createApiAction } from 'api'
import { userLoggedOut } from 'core/session/slices/session'
import {
  getComplianceHistoryOverview,
  getPersonalInformation,
  changeUserPassword,
  changeUserEmail,
} from 'modules/Profile/api/profileService'
import { namespace } from 'modules/Profile/localization'
import { ChangePasswordParams, ChangeEmailFormParams } from 'modules/Profile/types'
import {
  ProfileState,
  ApiComplianceHistoryResponse,
  ApiPersonalInformationResponse,
  ApiChangePasswordResponse,
  ApiChangeEmailResponse,
} from './types'

const { i18n } = localization

const I18_PATH = `${namespace}:profile`

const initialState: ProfileState = {
  complianceHistory: {},
  personalInformation: {},
  changePassword: {},
  changeEmail: {},
}

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    complianceHistoryLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiComplianceHistoryResponse>>
    ) {
      state.complianceHistory = action.payload
    },
    personalInformationLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiPersonalInformationResponse>>
    ) {
      state.personalInformation = action.payload
    },
    changePasswordProcessed(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiChangePasswordResponse>>
    ) {
      state.changePassword = action.payload
    },
    clearChangePassword(state) {
      state.changePassword = initialState.changePassword
    },
    changeEmailProcessed(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiChangeEmailResponse>>
    ) {
      state.changeEmail = action.payload
    },
    clearChangeEmail(state) {
      state.changeEmail = initialState.changeEmail
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  complianceHistoryLoaded,
  personalInformationLoaded,
  changePasswordProcessed,
  changeEmailProcessed,
  clearChangeEmail,
  clearChangePassword,
} = profileSlice.actions

export default profileSlice.reducer

export const resetChangeEmailProcess = clearChangeEmail
export const resetChangePasswordProcess = clearChangePassword

export const loadComplianceHistoryOverview = () => {
  return createApiAction({
    apiCalls: {
      historyOverview: getComplianceHistoryOverview,
    },
    payloadAction: complianceHistoryLoaded,
  })
}

export const loadPersonalInformation = () => {
  return createApiAction({
    apiCalls: {
      information: getPersonalInformation,
    },
    payloadAction: personalInformationLoaded,
  })
}

const handleChangeEmailProcess =
  (response: ApiMiddlewarePayload<ApiChangeEmailResponse>): AppThunk =>
  async dispatch => {
    const { error, result } = response
    if (!error && result) {
      dispatch(loadPersonalInformation())
    }
    dispatch(changeEmailProcessed(response))
  }

export const changeEmail = (formData: ChangeEmailFormParams) => {
  return createApiAction({
    apiCalls: {
      email: () => changeUserEmail(formData),
    },
    payloadAction: changeEmailProcessed,
    successMessage: i18n.t(`${I18_PATH}.accountInfoForm.changeSuccess`),
    nextAction: handleChangeEmailProcess,
  })
}

export const changePassword = (formData: ChangePasswordParams) => {
  return createApiAction({
    apiCalls: {
      password: () => changeUserPassword(formData),
    },
    payloadAction: changePasswordProcessed,
    successMessage: i18n.t(`${I18_PATH}.changePasswordForm.changeSuccess`),
  })
}
